/**
 * NorthstarShell — the persistent Northstar experience.
 *
 * Gated to `activated == true`. Non-activated members are routed away
 * (delayed → /portal/holding; everyone else → /portal). Never renders
 * a shell for anyone who is not activated.
 *
 * Phase 2A: responsive across desktop / tablet / narrow / mobile.
 * Sizing lives in `useNorthstarSizing`. Chrome (top brand + search +
 * back-to-portal) reflows between horizontal and stacked layouts as
 * viewport narrows. The clock-face + equal-orb rules are preserved.
 */
import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import { useActivation } from "../../hooks/useActivation";
import { useNorthstarSizing } from "../../hooks/useNorthstarSizing";
import { api } from "../../lib/auth";
import DeepSpaceBackground from "../../components/northstar/DeepSpaceBackground";
import IdentityCenter from "../../components/northstar/IdentityCenter";
import OrbCanvas from "../../components/northstar/OrbCanvas";
import OrbSearch from "../../components/northstar/OrbSearch";

export default function NorthstarShell() {
  const { data: activation, loading } = useActivation();
  const [profile, setProfile] = useState(null);
  const sizing = useNorthstarSizing();
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    api.get("/northstar/profile").then((r) => setProfile(r.data)).catch(() => {});
    // Clear any stale "came from Northstar" origin — we're at home now.
    try { sessionStorage.removeItem("northstar:origin"); } catch {}
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#040816]" data-testid="northstar-shell-loading">
        <Heart className="w-8 h-8 text-[#7ED493] animate-pulse" />
      </div>
    );
  }
  if (!activation) return <Navigate to="/portal" replace />;
  if (!activation.northstar_access_granted) {
    if (activation.should_render_holding_view) return <Navigate to="/portal/holding" replace />;
    return <Navigate to="/portal" replace />;
  }

  const awakeningSeen = !!profile?.awakening_seen_at;
  // The awakening ceremony copy ("You're connected. Now let's build your
  // Northstar.") is buddy-connection-specific. Intake-only members
  // therefore go straight to the shell and skip the welcome interstitial.
  if (profile && activation.status === "activated" && !awakeningSeen) {
    return <Navigate to="/northstar/welcome" replace />;
  }

  const stacked = sizing.headerLayout === "stacked";

  return (
    <div
      className="min-h-screen text-white relative overflow-x-hidden bg-[#040816]"
      data-testid="northstar-shell"
      data-viewport-tier={sizing.name}
    >
      <DeepSpaceBackground />

      {/* Top chrome — horizontal on ≥768px, stacked on narrow/mobile */}
      <header
        className="relative z-20 px-4 sm:px-6 pt-4 sm:pt-5 pb-2"
        data-testid="shell-header"
        data-header-layout={sizing.headerLayout}
      >
        {stacked ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#7ED493]" data-testid="shell-brand">
                Northstar
              </p>
              <Link
                to="/portal"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-white active:text-white transition-colors py-1.5 px-2 -mr-2"
                data-testid="shell-back-to-portal"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Portal
              </Link>
            </div>
            <div className="flex items-center justify-between gap-2">
              <OrbSearch />
              <Link
                to="/northstar/ask"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#7ED493]/15 border border-[#7ED493]/40 hover:bg-[#7ED493]/25 text-[11px] uppercase tracking-[0.2em] text-[#7ED493] py-1.5 px-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493]"
                data-testid="shell-ask-northstar"
              >
                Ask
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs uppercase tracking-[0.28em] text-[#7ED493]" data-testid="shell-brand">
              Northstar
            </p>
            <OrbSearch />
            <div className="flex items-center gap-3">
              <Link
                to="/northstar/ask"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#7ED493]/15 border border-[#7ED493]/40 hover:bg-[#7ED493]/25 text-xs uppercase tracking-[0.2em] text-[#7ED493] py-1.5 px-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493]"
                data-testid="shell-ask-northstar"
              >
                Ask Northstar
              </Link>
              <Link
                to="/portal"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                data-testid="shell-back-to-portal"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Portal
              </Link>
            </div>
          </div>
        )}
      </header>

      <main
        className="relative z-10"
        data-reduced-motion={reducedMotion ? "true" : "false"}
      >
        {/* Orbital field — fills the viewport under the identity.
            The whole scene is translated by `sceneShiftY` so it never
            enters the header exclusion zone on stacked tiers and
            collapses the excess top band on tablet. */}
        <div
          className="fixed inset-0 flex items-center justify-center pointer-events-none"
          style={{ transform: `translateY(${sizing.sceneShiftY}px)` }}
        >
          <div className="w-screen h-screen pointer-events-auto">
            <OrbCanvas
              centerSize={sizing.identity}
              orbSize={sizing.orb}
              columnX={sizing.columnX}
              rowYs={sizing.rowYs}
              emblemTextGap={sizing.emblemTextGap}
              textMaxW={sizing.textMaxW}
              nameToDescGap={sizing.nameToDescGap}
              nameSize={sizing.nameSize}
              descSize={sizing.descSize}
              descAtRest={sizing.descAtRest}
              labelStack={sizing.labelStack}
              tier={sizing.name}
              matrixInner={sizing.matrixInner}
              matrixOuter={sizing.matrixOuter}
            />
          </div>
        </div>

        {/* Identity — always centered on the authoritative axis, shifted
            by the same `sceneShiftY` as the orbital field so name +
            subtitle stay locked to the portrait's vertical hierarchy. */}
        <div
          className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none"
          style={{ zIndex: 50, transform: `translateY(${sizing.sceneShiftY}px)` }}
        >
          <div className="pointer-events-auto flex flex-col items-center">
            <IdentityCenter
              profile={profile}
              size={sizing.identity}
              haloExtend={sizing.portraitHaloExtend}
            />
            <p
              className={`${stacked ? "mt-2 text-base" : "mt-6 text-3xl"} font-semibold tracking-tight text-white text-center`}
              data-testid="shell-identity-name"
            >
              {profile?.name || "You"}
            </p>
            <p className={`${stacked ? "mt-0.5 text-[11px]" : "mt-1.5 text-sm"} tracking-wide text-white/60 text-center`}>
              At the center of your Northstar.
            </p>
          </div>
        </div>

        <div style={{ height: "100vh" }} />
      </main>
    </div>
  );
}
